import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { X, Upload, ZoomIn, ZoomOut, Trash2, Check, Loader2, Move, RotateCcw, Image as ImageIcon } from 'lucide-react';
import { unwrapImg, proxyImg } from '../lib/imgUrl';

/**
 * ProductDesignerModal — multi-view mockup editor.
 *
 * Renders the garment photo for each of 4 print locations (Front, Back, Left
 * Sleeve, Right Sleeve) and lets the admin overlay a logo/artwork on each
 * INDEPENDENTLY. On save, a composite PNG is rendered per view and all four
 * are persisted via PUT /proposals/{id}/items/{idx}/mockup-design.
 *
 * Props:
 *   open     – bool
 *   item     – proposal item. Uses:
 *                • item.views: { front, back, left, right }  (canvas base images)
 *                • item.image_url                            (fallback base image)
 *                • item.mockup_designs: { front, back, left, right }
 *                     each entry is { logo_base64, position:{x,y,size} } | null
 *   onClose  – () => void
 *   onSave   – async ({
 *                mockup_views:   { front, back, left, right }, // base64 PNGs (only rendered ones)
 *                mockup_designs: { front, back, left, right }, // editable state per view
 *                mockup_url:     string,                       // small thumbnail (first designed view)
 *              }) => void
 *   saving   – bool
 */
const VIEWS = [
  { key: 'front', label: 'Front' },
  { key: 'back',  label: 'Back' },
  { key: 'left',  label: 'Left' },
  { key: 'right', label: 'Right' },
];

const DEFAULT_POS = { x: 50, y: 45, size: 22 };

const ProductDesignerModal = ({ open, item, onClose, onSave, saving = false }) => {
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);

  // Which tab (view) is currently active.
  const [activeView, setActiveView] = useState('front');
  // Per-view editable state. { front: {logo, pos}, back: {...}, ... }
  const [designs, setDesigns] = useState({});
  // Per-view custom garment photo override (base64) — used when SanMar's CDN
  // is missing / shows the "IMAGE NOT YET AVAILABLE FOR SELECTED COLOR"
  // placeholder. Admin uploads a real photo to replace it for that view only.
  const [viewsOverride, setViewsOverride] = useState({ front: null, back: null, left: null, right: null });
  // Drag state (shared, applies to the active view).
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  // Phones / small tablets: stack the canvas above the controls so the garment stays full-width.
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const h = () => setIsNarrow(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // Seed on open — hydrate from any previously saved multi-view designs, or
  // fall back to the legacy single-mockup fields so old items still edit.
  useEffect(() => {
    if (!open) return;
    const seed = { front: null, back: null, left: null, right: null };
    const saved = item?.mockup_designs || {};
    Object.keys(seed).forEach((k) => {
      if (saved[k]?.logo_base64) {
        seed[k] = { logo: saved[k].logo_base64, pos: saved[k].position || DEFAULT_POS };
      }
    });
    // Back-compat: if only legacy fields exist, seed them onto the front tab.
    if (!seed.front && item?.logo_base64) {
      seed.front = { logo: item.logo_base64, pos: item.logo_position || DEFAULT_POS };
    }
    setDesigns(seed);
    setViewsOverride({
      front: item?.views_override?.front || null,
      back:  item?.views_override?.back  || null,
      left:  item?.views_override?.left  || null,
      right: item?.views_override?.right || null,
    });
    setActiveView('front');
  }, [open, item?.id, item?.mockup_designs, item?.views_override, item?.logo_base64, item?.logo_position]);

  // Resolve the garment photo URL for each view. Custom uploads win over the
  // SanMar CDN URL so a broken/placeholder image can be swapped in-place.
  const viewImages = useMemo(() => {
    const v = item?.views || {};
    const fallback = unwrapImg(item?.image_url);
    return {
      front: viewsOverride.front || unwrapImg(v.front) || fallback,
      back:  viewsOverride.back  || unwrapImg(v.back)  || fallback,
      left:  viewsOverride.left  || unwrapImg(v.left)  || fallback,
      right: viewsOverride.right || unwrapImg(v.right) || fallback,
    };
  }, [item?.views, item?.image_url, viewsOverride]);

  const activeDesign = designs[activeView];
  const activeImage = viewImages[activeView];
  const activePos = activeDesign?.pos || DEFAULT_POS;
  const activeLogo = activeDesign?.logo || null;

  const setActivePos = (updater) => {
    setDesigns((prev) => {
      const cur = prev[activeView] || { logo: null, pos: DEFAULT_POS };
      const next = typeof updater === 'function' ? updater(cur.pos) : updater;
      return { ...prev, [activeView]: { logo: cur.logo, pos: next } };
    });
  };
  const setActiveLogo = (logoData) => {
    setDesigns((prev) => {
      const cur = prev[activeView] || { logo: null, pos: DEFAULT_POS };
      return { ...prev, [activeView]: { logo: logoData, pos: cur.pos || DEFAULT_POS } };
    });
  };

  const onUploadLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error('Logo too large — keep under 6MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setActiveLogo(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Uploading a custom garment photo replaces the SanMar image for the ACTIVE
  // view only — used when SanMar returns the "IMAGE NOT YET AVAILABLE" tile
  // for a color it hasn't shot yet. Downscale to keep the base64 payload
  // manageable so 4 overrides + composites stay well under Mongo's 16MB doc
  // cap (target ~1200px longest edge, JPEG at 82% quality ≈ 150-350KB).
  const onReplacePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Photo too large — keep under 15MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        const ratio = Math.min(MAX / img.width, MAX / img.height, 1);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d');
        // White background so transparent PNGs still print/preview cleanly.
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = c.toDataURL('image/jpeg', 0.82);
        setViewsOverride((prev) => ({ ...prev, [activeView]: dataUrl }));
        toast.success(`${VIEWS.find((v) => v.key === activeView)?.label} photo replaced`);
      };
      img.onerror = () => toast.error('Could not read image file');
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  const revertPhoto = () => {
    setViewsOverride((prev) => ({ ...prev, [activeView]: null }));
    toast.success('Reverted to SanMar photo');
  };

  // Drag handlers (mouse + touch).
  const startDrag = (e) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const isTouch = e.touches?.length > 0;
    const cx = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    const px = (activePos.x / 100) * rect.width;
    const py = (activePos.y / 100) * rect.height;
    setDragOffset({ x: cx - px, y: cy - py });
    setDragging(true);
  };
  const onDrag = (e) => {
    if (!dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const isTouch = e.touches?.length > 0;
    const cx = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    const nx = ((cx - dragOffset.x) / rect.width) * 100;
    const ny = ((cy - dragOffset.y) / rect.height) * 100;
    setActivePos((p) => ({ ...p, x: Math.max(5, Math.min(95, nx)), y: Math.max(5, Math.min(95, ny)) }));
  };
  const endDrag = () => setDragging(false);

  // Composite one view (garment + positioned logo) → PNG data URL.
  const renderOneView = (imageUrl, design) => new Promise((resolve, reject) => {
    if (!imageUrl) { resolve(null); return; }
    const canvas = document.createElement('canvas');
    canvas.width = 1000;
    canvas.height = 1200;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const proxied = proxyImg(imageUrl);
    const garment = new Image();
    garment.crossOrigin = 'anonymous';
    garment.onload = () => {
      const ratio = Math.min(canvas.width / garment.width, canvas.height / garment.height);
      const gw = garment.width * ratio;
      const gh = garment.height * ratio;
      const gx = (canvas.width - gw) / 2;
      const gy = (canvas.height - gh) / 2;
      ctx.drawImage(garment, gx, gy, gw, gh);
      if (!design?.logo) {
        resolve(canvas.toDataURL('image/png'));
        return;
      }
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      logo.onload = () => {
        const pos = design.pos || DEFAULT_POS;
        const lw = (pos.size / 100) * gw;
        const lRatio = logo.height / Math.max(logo.width, 1);
        const lh = lw * lRatio;
        const lx = gx + (pos.x / 100) * gw - lw / 2;
        const ly = gy + (pos.y / 100) * gh - lh / 2;
        ctx.drawImage(logo, lx, ly, lw, lh);
        resolve(canvas.toDataURL('image/png'));
      };
      logo.onerror = () => resolve(canvas.toDataURL('image/png'));
      logo.src = design.logo;
    };
    garment.onerror = () => reject(new Error('Could not load garment image (CORS or 404). Try re-attaching the product.'));
    garment.src = proxied;
  });

  const handleSave = async () => {
    if (!viewImages.front && !item?.image_url) {
      toast.error('This item has no product photo yet. Attach a product first.');
      return;
    }
    try {
      // Render a composite PNG for any view that has EITHER a logo OR a
      // custom garment override — so the ItemCard picks up the replacement
      // photo even before the admin adds a logo. Untouched views are null.
      const mockupViews = {};
      const mockupDesigns = {};
      for (const v of VIEWS) {
        const d = designs[v.key];
        mockupDesigns[v.key] = d?.logo
          ? { logo_base64: d.logo, position: d.pos || DEFAULT_POS }
          : null;
        const hasOverride = !!viewsOverride[v.key];
        if (d?.logo || hasOverride) {
          const png = await renderOneView(viewImages[v.key], d);
          if (png) mockupViews[v.key] = png;
        }
      }
      // Small thumbnail — first designed OR overridden view, used on ItemCard.
      const firstKey = VIEWS.find((v) => mockupViews[v.key])?.key;
      const thumb = firstKey ? mockupViews[firstKey] : null;
      await onSave({
        mockup_views: mockupViews,
        mockup_designs: mockupDesigns,
        mockup_url: thumb,
        views_override: viewsOverride,
      });
    } catch (e) {
      toast.error(e.message || 'Could not render mockup');
    }
  };

  if (!open) return null;

  const designedCount = VIEWS.filter((v) => designs[v.key]?.logo).length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70 }} onClick={saving ? undefined : onClose} data-testid="product-designer-modal">
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: '92vw', maxWidth: 960, maxHeight: '92vh', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 80px rgba(0,0,0,0.35)' }}>
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0F172A' }}>Design on Product</div>
            <div style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>
              {[item?.brand, item?.style, item?.color].filter(Boolean).join(' · ') || 'Selected item'}
              {designedCount > 0 && (
                <span style={{ marginLeft: 8, background: '#DCFCE7', color: '#166534', padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                  {designedCount} / 4 locations
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} data-testid="designer-close-btn" disabled={saving} style={{ background: 'transparent', border: 'none', padding: 4, cursor: saving ? 'not-allowed' : 'pointer', display: 'flex' }}>
            <X size={20} color="#64748B" />
          </button>
        </div>

        {/* View tabs — one per print location */}
        <div style={{ display: 'flex', gap: 4, padding: '10px 22px 0 22px', background: '#fff', flexShrink: 0 }} data-testid="designer-view-tabs">
          {VIEWS.map((v) => {
            const isActive = v.key === activeView;
            const hasLogo = !!designs[v.key]?.logo;
            return (
              <button
                key={v.key}
                onClick={() => setActiveView(v.key)}
                data-testid={`designer-view-${v.key}`}
                style={{
                  flex: 1, padding: '10px 8px', border: 'none',
                  background: isActive ? '#0F172A' : '#F1F5F9',
                  color: isActive ? '#fff' : '#475569',
                  fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
                  cursor: 'pointer', borderTopLeftRadius: 6, borderTopRightRadius: 6,
                  transition: 'background 0.15s', position: 'relative',
                }}
              >
                {v.label}
                {hasLogo && (
                  <span style={{ position: 'absolute', top: 4, right: 6, width: 8, height: 8, borderRadius: 999, background: '#10B981', boxShadow: '0 0 0 2px #fff' }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ padding: isNarrow ? 12 : 22, display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : '1fr 220px', gap: isNarrow ? 14 : 22, overflowY: 'auto', flex: 1, background: '#F8FAFC' }}>
          {/* Canvas */}
          <div
            ref={canvasRef}
            onMouseMove={onDrag}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onTouchMove={onDrag}
            onTouchEnd={endDrag}
            style={{ position: 'relative', width: '100%', aspectRatio: '4/5', background: '#fff', borderRadius: 8, border: '1px solid #E2E8F0', overflow: 'hidden', userSelect: 'none' }}
            data-testid="designer-canvas"
          >
            {activeImage ? (
              <img src={activeImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} draggable={false} />
            ) : (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', fontSize: 13, textAlign: 'center', padding: 20 }}>
                No {activeView} photo available for this color. Attach a product with per-angle photos.
              </div>
            )}
            {activeLogo && activeImage && (
              <img
                src={activeLogo}
                onMouseDown={startDrag}
                onTouchStart={startDrag}
                draggable={false}
                data-testid="designer-logo"
                style={{
                  position: 'absolute',
                  left: `${activePos.x}%`,
                  top: `${activePos.y}%`,
                  width: `${activePos.size}%`,
                  transform: 'translate(-50%, -50%)',
                  filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.35))',
                  cursor: 'move',
                  touchAction: 'none',
                }}
              />
            )}
            {!activeLogo && activeImage && (
              <button
                onClick={() => fileInputRef.current?.click()}
                data-testid="designer-upload-hint"
                style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', background: 'rgba(15,23,42,0.75)', color: '#fff', border: '1px dashed rgba(255,255,255,0.4)', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: '0.03em' }}
              >
                <Upload size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Upload Logo for {VIEWS.find((v) => v.key === activeView)?.label}
              </button>
            )}
          </div>

          {/* Tools */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={onUploadLogo} style={{ display: 'none' }} data-testid="designer-file-input" />
            <input ref={photoInputRef} type="file" accept="image/*" onChange={onReplacePhoto} style={{ display: 'none' }} data-testid="designer-photo-input" />
            <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {VIEWS.find((v) => v.key === activeView)?.label} location
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              data-testid="designer-upload-btn"
              style={{ background: '#0F172A', color: '#fff', border: 'none', padding: '10px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <Upload size={13} /> {activeLogo ? 'Replace Logo' : 'Upload Logo'}
            </button>

            {/* Custom garment-photo override — for when SanMar's per-color
                image is missing / showing "IMAGE NOT YET AVAILABLE". */}
            <div style={{ borderTop: '1px dashed #CBD5E1', paddingTop: 10, marginTop: 4 }}>
              <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
                Garment photo
              </div>
              <button
                onClick={() => photoInputRef.current?.click()}
                data-testid="designer-replace-photo-btn"
                style={{ width: '100%', background: viewsOverride[activeView] ? '#EFF6FF' : '#fff', border: `1px solid ${viewsOverride[activeView] ? '#3B82F6' : '#E2E8F0'}`, padding: '9px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: viewsOverride[activeView] ? '#1D4ED8' : '#475569', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                title="Upload a custom photo when SanMar's image is missing or wrong"
              >
                <ImageIcon size={13} /> {viewsOverride[activeView] ? 'Replace Custom Photo' : 'Upload Custom Photo'}
              </button>
              {viewsOverride[activeView] && (
                <button
                  onClick={revertPhoto}
                  data-testid="designer-revert-photo-btn"
                  style={{ width: '100%', marginTop: 6, background: '#fff', border: '1px solid #E2E8F0', padding: '7px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, color: '#64748B', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <RotateCcw size={11} /> Revert to SanMar Photo
                </button>
              )}
            </div>

            {activeLogo && (
              <>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => setActivePos((p) => ({ ...p, size: Math.max(6, p.size - 3) }))}
                    data-testid="designer-shrink-btn"
                    style={{ flex: 1, background: '#fff', border: '1px solid #E2E8F0', padding: '9px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Shrink"
                  >
                    <ZoomOut size={15} color="#475569" />
                  </button>
                  <button
                    onClick={() => setActivePos((p) => ({ ...p, size: Math.min(80, p.size + 3) }))}
                    data-testid="designer-enlarge-btn"
                    style={{ flex: 1, background: '#fff', border: '1px solid #E2E8F0', padding: '9px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Enlarge"
                  >
                    <ZoomIn size={15} color="#475569" />
                  </button>
                </div>
                <button
                  onClick={() => setActivePos(DEFAULT_POS)}
                  data-testid="designer-reset-btn"
                  style={{ background: '#fff', border: '1px solid #E2E8F0', padding: '9px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11, color: '#475569', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <RotateCcw size={13} /> Reset Position
                </button>
                <div style={{ fontSize: 11, color: '#64748B', lineHeight: 1.5, padding: '10px 12px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, display: 'flex', gap: 6 }}>
                  <Move size={13} color="#94A3B8" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>Drag the logo to fine-tune its position. Each view has its own logo.</span>
                </div>
                <button
                  onClick={() => setActiveLogo(null)}
                  data-testid="designer-remove-logo-btn"
                  style={{ background: '#fff', border: '1px solid #FCA5A5', padding: '9px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11, color: '#B91C1C', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Trash2 size={12} /> Remove from {VIEWS.find((v) => v.key === activeView)?.label}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, background: '#fff' }}>
          <div style={{ fontSize: 12, color: '#64748B' }}>
            {designedCount === 0
              ? 'Upload a logo on any view to save a mockup.'
              : `${designedCount} location${designedCount === 1 ? '' : 's'} designed — customer will see all mockups on the payment page and PDF.`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} disabled={saving} style={{ padding: '9px 16px', background: '#fff', border: '1px solid #E2E8F0', borderRadius: 6, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, color: '#475569', fontWeight: 500 }}>Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="designer-save-btn"
              style={{ padding: '9px 20px', background: '#0F172A', color: '#fff', border: 'none', borderRadius: 6, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save All Mockups
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDesignerModal;
